import React from 'react';
import { Order, Client, OrderStatus } from '../../types';
import { ShoppingBagIcon } from '../icons';

interface OrderListViewProps {
    orders: Order[];
    clients: Client[];
    onSelectOrder: (client: Client, orderId: string) => void;
}

const STATUS_LABELS: Record<OrderStatus, string> = {
    pending: 'En attente',
    in_progress: 'En cours',
    ready: 'Prêt',
    delivered: 'Livré',
    cancelled: 'Annulé',
};

const STATUS_CLASSES: Record<OrderStatus, string> = {
    pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
    in_progress: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
    ready: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300',
    delivered: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
    cancelled: 'bg-gray-100 text-gray-700 dark:bg-slate-700 dark:text-gray-300',
};

export const OrderListView: React.FC<OrderListViewProps> = ({ orders, clients, onSelectOrder }) => {
    const activeOrders = orders.filter(o => !o.isDeleted).sort((a, b) => {
        const dateA = a.dueDate ? new Date(a.dueDate).getTime() : 0;
        const dateB = b.dueDate ? new Date(b.dueDate).getTime() : 0;
        return dateA - dateB;
    });

    if (activeOrders.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-20 px-4 text-center">
                <div className="w-20 h-20 bg-gray-100 dark:bg-slate-800 rounded-full flex items-center justify-center mb-4">
                    <ShoppingBagIcon className="w-10 h-10 text-gray-400 dark:text-slate-500" />
                </div>
                <h3 className="text-lg font-medium t-text-primary">Aucune commande</h3>
                <p className="t-text-secondary mt-2">Les commandes en cours apparaîtront ici.</p>
            </div>
        );
    }

    return (
        <div className="px-4 py-6 space-y-4 pb-24">
            <h2 className="text-xl font-bold t-text-primary mb-6">Toutes les commandes</h2>
            
            <div className="space-y-3">
                {activeOrders.map(order => {
                    const client = clients.find(c => c.id === order.clientId);
                    if (!client) return null;

                    return (
                        <div 
                            key={order.id}
                            onClick={() => onSelectOrder(client, order.id)}
                            className="t-card p-4 active:scale-[0.98] transition-transform"
                        >
                             <div className="flex justify-between items-start mb-2">
                                <div>
                                    <h4 className="font-bold t-text-primary">{client.name}</h4>
                                    <p className="text-xs t-text-secondary">{order.outfitType}</p>
                                </div>
                                <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase ${STATUS_CLASSES[order.status]}`}>
                                    {STATUS_LABELS[order.status]}
                                </span>
                            </div>
                             
                            <div className="flex justify-between items-end mt-4">
                                <div className="text-xs t-text-secondary">
                                    Échéance: {order.dueDate ? new Date(order.dueDate).toLocaleDateString('fr-FR') : 'Non définie'}
                                </div>
                                <div className="text-sm font-bold text-brand-primary">
                                    {order.negotiatedAmount ? `${order.negotiatedAmount.toLocaleString('fr-FR')} FCFA` : '—'}
                                </div>
                            </div>
                             
                            {order.paid && (
                                <div className="mt-2 pt-2 border-t border-gray-100 dark:border-slate-700/50 flex items-center gap-1">
                                    <span className="text-[10px] text-emerald-600 font-medium">✓ Payé</span>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
